/**
 * Error Handler Utilities
 * 
 * Centralized error handling utility that maps backend error codes to i18n messages
 * and displays user-friendly notifications:
 * - API error handling
 * - Success notification handling
 * - Error code mapping
 * - i18n integration
 * 
 * @module utils/errorHandler
 */

/**
 * Handle API errors with user-friendly notifications
 * 
 * Maps common error codes to i18n keys and displays error notifications.
 * Logs errors to console in development mode.
 * 
 * @param {Error} error - The error object
 * @param {Function} enqueueSnackbar - Notistack enqueueSnackbar function
 * @param {Function} t - i18next translation function
 * @param {string} [defaultMessage='common.errorOccurred'] - Default message key if error code not mapped
 * @returns {void}
 */
export const handleApiError = (error, enqueueSnackbar, t, defaultMessage = 'common.errorOccurred') => {
  const errorCode = error?.response?.data?.error_code || error?.code;
  const errorMessage = error?.response?.data?.message || error?.message;
  
  // Map common error codes to i18n keys
  const errorMap = {
    'UNAUTHORIZED': 'errors.unauthorized',
    'FORBIDDEN': 'errors.forbidden',
    'NOT_FOUND': 'errors.notFound',
    'VALIDATION_ERROR': 'errors.validationError',
    'DUPLICATE_ENTRY': 'errors.duplicateEntry',
    'NETWORK_ERROR': 'errors.networkError',
    'TIMEOUT': 'errors.timeout',
  };
  
  const messageKey = errorMap[errorCode] || defaultMessage;
  const message = t(messageKey, { defaultValue: errorMessage || t(defaultMessage) });
  
  enqueueSnackbar(message, {
    variant: 'error',
    autoHideDuration: 5000,
  });
  
  // Log to console in development only
  // eslint-disable-next-line no-undef
  if (typeof process !== 'undefined' && process.env && process.env.NODE_ENV === 'development') {
    console.error('[API Error]', {
      code: errorCode,
      message: errorMessage,
      error,
    });
  }
};

/**
 * Handle mutation success with notification
 * @param {string} messageKey - i18n key for success message
 * @param {Function} enqueueSnackbar - Notistack enqueueSnackbar function
 * @param {Function} t - i18next translation function
 */
export const handleMutationSuccess = (messageKey, enqueueSnackbar, t) => {
  enqueueSnackbar(t(messageKey), {
    variant: 'success',
    autoHideDuration: 3000,
  });
};

/**
 * Create standardized onError handler for React Query mutations
 * @param {Function} enqueueSnackbar - Notistack enqueueSnackbar function
 * @param {Function} t - i18next translation function
 * @param {string} defaultMessage - Default message key
 * @returns {Function} Error handler function
 */
export const createMutationErrorHandler = (enqueueSnackbar, t, defaultMessage) => {
  return (error) => handleApiError(error, enqueueSnackbar, t, defaultMessage);
};

/**
 * Create standardized onSuccess handler for React Query mutations
 * @param {Function} enqueueSnackbar - Notistack enqueueSnackbar function
 * @param {Function} t - i18next translation function
 * @param {string} messageKey - Success message key
 * @returns {Function} Success handler function
 */
export const createMutationSuccessHandler = (enqueueSnackbar, t, messageKey) => {
  return () => handleMutationSuccess(messageKey, enqueueSnackbar, t);
};
